import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Tree,
  Typography,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';
import { ChevronRight, FolderTree, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { useDebounce } from '@shared/hooks/useDebounce';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { categoriesApi } from '@api/categories';
import { recordAuditAction } from '@features/audit/recordAction';
import { CategoryDrawer } from '../components/CategoryDrawer';
import type { Category, CategoryTreeNode } from '../types';
import { buildCategoryTree } from '../utils/tree';

const { Text } = Typography;

function filterTree(tree: CategoryTreeNode[], q: string): CategoryTreeNode[] {
  if (!q) return tree;
  const needle = q.toLowerCase();
  const filter = (nodes: CategoryTreeNode[]): CategoryTreeNode[] => {
    const out: CategoryTreeNode[] = [];
    for (const n of nodes) {
      const children = filter(n.children);
      const match = n.name.toLowerCase().includes(needle) || n.slug.toLowerCase().includes(needle);
      if (match || children.length) out.push({ ...n, children });
    }
    return out;
  };
  return filter(tree);
}

interface NodeTitleProps {
  node: CategoryTreeNode;
  languageCode?: string;
  onEdit: (c: Category) => void;
  onAddChild: (c: Category) => void;
  onDelete: (c: Category) => void;
  deletingId?: string;
}

function NodeTitle({ node, languageCode, onEdit, onAddChild, onDelete, deletingId }: NodeTitleProps) {
  const { t } = useTranslation(['categories', 'common']);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span style={{ fontWeight: 500 }}>{node.name}</span>
        <Tag style={{ marginInlineStart: 0 }}>{node.slug}</Tag>
        {languageCode ? (
          <Tag color="blue" style={{ marginInlineStart: 0 }}>
            {languageCode}
          </Tag>
        ) : null}
        {node.description ? (
          <Text type="secondary" ellipsis style={{ fontSize: 12, maxWidth: 360 }}>
            {node.description}
          </Text>
        ) : null}
      </div>
      <Space>
        <Tooltip title={t('actions.addChild')}>
          <Button size="small" icon={<Plus size={13} />} onClick={() => onAddChild(node)} />
        </Tooltip>
        <Tooltip title={t('common:actions.edit')}>
          <Button size="small" icon={<Pencil size={13} />} onClick={() => onEdit(node)} />
        </Tooltip>
        <Popconfirm
          title={t('delete.title')}
          description={t('delete.confirm', { name: node.name })}
          okText={t('common:actions.delete')}
          okButtonProps={{ danger: true, loading: deletingId === node.id }}
          cancelText={t('common:actions.cancel')}
          onConfirm={() => onDelete(node)}
        >
          <Tooltip title={t('common:actions.delete')}>
            <Button size="small" danger icon={<Trash2 size={13} />} />
          </Tooltip>
        </Popconfirm>
      </Space>
    </div>
  );
}

export function CategoriesPage() {
  const { t } = useTranslation(['categories', 'common']);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [languageId, setLanguageId] = useState<string | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [presetParent, setPresetParent] = useState<Category | null>(null);

  const { data: activeLanguages = [] } = useActiveLanguages();
  const langCodeById = useMemo(
    () => new Map(activeLanguages.map((l) => [l.id, l.code])),
    [activeLanguages],
  );

  const { data, isFetching } = useQuery({
    queryKey: ['categories', { languageId }],
    queryFn: () => categoriesApi.list({ languageId }),
  });

  const tree = useMemo(() => buildCategoryTree(data?.items ?? []), [data]);
  const filtered = useMemo(() => filterTree(tree, debouncedSearch), [tree, debouncedSearch]);
  const totalCount = data?.items.length ?? 0;
  const rootCount = useMemo(() => tree.length, [tree]);

  const deleteMutation = useMutation({
    mutationFn: (c: Category) => categoriesApi.remove(c.id),
    onSuccess: async (_d, c) => {
      toast.success(t('toast.deleted', { name: c.name }));
      await qc.invalidateQueries({ queryKey: ['categories'] });
      void recordAuditAction({
        action: 'category.delete',
        resource: 'category',
        resourceId: c.id,
        summary: t('audit.deleteSummary', { name: c.name }),
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const treeData = useMemo<DataNode[]>(() => {
    const toNodes = (nodes: CategoryTreeNode[]): DataNode[] =>
      nodes.map((n) => ({
        key: n.id,
        title: (
          <NodeTitle
            node={n}
            languageCode={langCodeById.get(n.languageId)}
            onEdit={(c) => {
              setEditing(c);
              setPresetParent(null);
              setDrawerOpen(true);
            }}
            onAddChild={(parent) => {
              setEditing(null);
              setPresetParent(parent);
              setDrawerOpen(true);
            }}
            onDelete={(c) => deleteMutation.mutate(c)}
            deletingId={deleteMutation.isPending ? deleteMutation.variables?.id : undefined}
          />
        ),
        children: n.children.length ? toNodes(n.children) : undefined,
      }));
    return toNodes(filtered);
  }, [filtered, deleteMutation, langCodeById]);

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[
          { label: t('breadcrumb.home'), to: '/' },
          { label: t('breadcrumb.taxonomy') },
          { label: t('breadcrumb.categories') },
        ]}
        actions={
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => {
              setEditing(null);
              setPresetParent(null);
              setDrawerOpen(true);
            }}
          >
            {t('actions.new')}
          </Button>
        }
      />

      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <LanguagePicker
              value={languageId}
              onChange={setLanguageId}
              allowClear
              placeholder={t('common:contentLanguage.filterAll')}
              style={{ minWidth: 180 }}
            />
            <Input
              prefix={<Search size={14} />}
              placeholder={t('search.placeholder')}
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
          </div>

          <Alert
            type="info"
            showIcon
            icon={<FolderTree size={14} />}
            message={t('alert.title')}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                <Trans
                  t={t}
                  i18nKey="alert.summary"
                  values={{
                    total: totalCount,
                    root: rootCount,
                  }}
                  components={[<strong key="total" />, <strong key="root" />]}
                />
              </Text>
            }
            style={{ background: 'var(--ds-surface-2)' }}
          />

          {isFetching ? null : filtered.length === 0 ? (
            <Empty
              description={
                debouncedSearch
                  ? t('empty.notFound', { query: debouncedSearch })
                  : t('empty.none')
              }
            >
              {!debouncedSearch ? (
                <Button
                  type="primary"
                  icon={<Plus size={14} />}
                  onClick={() => {
                    setEditing(null);
                    setPresetParent(null);
                    setDrawerOpen(true);
                  }}
                >
                  {t('actions.create')}
                </Button>
              ) : null}
            </Empty>
          ) : (
            <Tree
              treeData={treeData}
              defaultExpandAll
              selectable={false}
              showLine
              switcherIcon={<ChevronRight size={14} />}
              blockNode
              className="ds-category-tree"
            />
          )}
        </Space>
      </Card>

      <CategoryDrawer
        open={drawerOpen}
        category={editing}
        initialParentId={editing ? undefined : presetParent?.id ?? null}
        allCategories={data?.items ?? []}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
          setPresetParent(null);
        }}
      />
    </div>
  );
}
